import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import { isNotFound } from "@/api/client";
import { listGateways } from "@/api/catalog";
import { listIntegrations } from "@/api/integrations";
import { listMeasurementPresets, presetAllowsTenant, presetKeys } from "@/api/measurementPresets";
import { getTenant, putStage, PutStageInput } from "@/api/tenants";
import { ConfirmModal } from "@/components/ConfirmModal";
import {
  Checkbox,
  ChoiceChip,
  Field,
  Fieldset,
  Input,
  Radio,
  Select,
} from "@/components/form";
import { IntegrationForm } from "@/components/IntegrationForm";
import { ErrorBox, MutationError, RefreshFailed, isLoadingOrPaused, loadFailure } from "@/components/Spinner";
import { TenantNotFound } from "@/components/TenantNotFound";
import type { AutoDeployPolicy, TenantOption } from "@/types/api";
import { Button, FormActionBar, FormPlaceholder, LoadingRegion, Page, PageHeader, Skeleton } from "@/components/ui";
import { useDirtyValues } from "@/hooks/useDirtyValues";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";

const OPTIONS: TenantOption[] = ["GrandCentral", "MOMA", "Flatiron"];

// A starter catalogue for the "Enabled ingest regions" checklist — not the
// backend's word on what's valid (it only checks the shape, `_validate_-
// enabled_ingest_regions` in models.py) and not exhaustive: edit this list as
// real deployments happen. A region already saved on the stage that isn't in
// it is still shown (regionRows below), never dropped, and the "Add region"
// field lets an operator reach any other AWS-region-shaped id today, before
// this list — or a real catalog entry, GatewaySelector-style — catches up.
const INGEST_REGION_CATALOG = ["eu-west-3", "eu-central-1", "us-east-1"];

// Backend stores ``allowed_*`` as ``{ <model>: [<version>, …] }``. The form
// only exposes the model dimension for now and emits a wildcard ``"*"`` per
// selected model. If an operator pinned specific semver versions out-of-band,
// saving the form will clobber them back to wildcard — acceptable as long as
// version-pinning is a power-user path.
function modelsToAllowMap(models: string[]): Record<string, string[]> | null {
  if (models.length === 0) return null;
  return Object.fromEntries(models.map((m) => [m, ["*"]]));
}

interface FormState extends PutStageInput {
  stage: string;
  pinned_customer_gw_build_id: string | null;
  allow_beta_gateway_builds: boolean;
  auto_update_customer_gateways: boolean;
  enabled_options: TenantOption[];
  allowed_gateway_types_models: string[] | null;
  allowed_customer_instances_models: string[] | null;
  diagnostics_ui_enabled: boolean;
  bug_tracker_enabled: boolean;
  companion_apps_enabled: boolean;
  advertising_enabled: boolean;
  mcp_enabled: boolean;
  audience_measurement_enabled: boolean;
  // null = the 90-day default (never the empty string, like the fields below).
  audience_measurement_ip_retention_days: number | null;
  measurement_presets: string[];
  // null = inherit the tenant default. Sent verbatim on save (the empty-string
  // strip in the mutation only drops "" — an explicit null clears the override,
  // a number sets it), so it must never be the empty string here.
  media_retention_days: number | null;
  // Milliseconds in state and on the wire; the input shows seconds. null =
  // inherit the tenant default (never the empty string).
  audience_live_threshold_ms: number | null;
  // Continuous-deployment policy. Always an enum value (never ""), so it
  // passes through the mutation's empty-string strip unchanged.
  auto_deploy: AutoDeployPolicy;
  // Narrowed from PutStageInput's optional form: the checklist always holds a
  // real array (never undefined), lowercased and deduped as each region is
  // added — see addRegion below.
  enabled_ingest_regions: string[];
  // Ids of the tenant's identity providers offered on this stage's sign-in
  // page — narrowed the same way (always a real array).
  enabled_identity_providers: string[];
}

const PULSESTATION_ROOT = "pulsestation.io";

// What the operator can change, in a shape that says the same thing when it says the same thing: a
// list whose order means nothing is sorted (switching an option off and on again moves it to the end),
// "no model" is the same whether it is null or empty, and the region text is the list it stands for.
function comparableValues(state: FormState) {
  const {
    enabled_options,
    allowed_gateway_types_models,
    allowed_customer_instances_models,
    enabled_ingest_regions,
    enabled_identity_providers,
    measurement_presets,
    ...rest
  } = state;
  return {
    ...rest,
    measurement_presets: [...measurement_presets].sort(),
    enabled_options: [...enabled_options].sort(),
    allowed_gateway_types_models: [...(allowed_gateway_types_models ?? [])].sort(),
    allowed_customer_instances_models: [...(allowed_customer_instances_models ?? [])].sort(),
    enabled_ingest_regions: [...enabled_ingest_regions].sort(),
    enabled_identity_providers: [...enabled_identity_providers].sort(),
  };
}

export function StageFormPage() {
  const { tenantId = "", stage } = useParams<{
    tenantId: string;
    stage?: string;
  }>();
  const isEdit = Boolean(stage);
  const auth = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const token = auth.user?.id_token ?? "";

  const existing = useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: () => getTenant(token, tenantId),
    enabled: !!token && !!tenantId,
  });

  const catalog = useQuery({
    queryKey: ["catalog", "gateways"],
    queryFn: () => listGateways(token),
    enabled: !!token,
  });

  // Les fournisseurs qu'un environnement peut redéfinir pour lui seul. Les
  // ``globalOnly`` sont lus par le control plane, qui répond à tous les
  // clients : une valeur rangée ici ne serait consultée par personne, et le
  // backend la refuse.
  const integrations = useQuery({
    queryKey: ["integrations"],
    queryFn: () => listIntegrations(token),
    enabled: !!token,
  });
  // Les presets de mesure qu'un stage peut activer : ceux ouverts au tenant
  // (``allowedTenants`` nul ou qui le nomme). Le backend le revérifie.
  const presets = useQuery({
    queryKey: presetKeys.list,
    queryFn: () => listMeasurementPresets(token),
    enabled: !!token,
  });
  const overridable = (integrations.data?.items ?? []).filter(
    (item) => !item.globalOnly,
  );
  const cloudInstances = Array.from(
    new Set(
      (catalog.data?.items ?? [])
        .filter((e) => e.kind === "cloud")
        .map((e) => e.model),
    ),
  );
  const customerInstances = Array.from(
    new Set(
      (catalog.data?.items ?? [])
        .filter((e) => e.kind === "customer")
        .map((e) => e.model),
    ),
  );

  const initial = isEdit
    ? existing.data?.stages.find((s) => s.stage === stage)
    : undefined;
  const declaredDirectoryIds = useMemo(
    () => (existing.data?.metadata.identityProviders ?? []).map((p) => p.id),
    [existing.data],
  );

  // Another stage on this tenant is already production — block creating a
  // second one. The check ignores the stage currently being edited.
  const otherProdStage = existing.data?.stages.find(
    (s) => s.isProduction && s.stage !== stage,
  );
  const prodLocked = Boolean(otherProdStage);

  const [state, setState] = useState<FormState>({
    stage: stage ?? "",
    aws_region: "eu-west-1",
    is_production: false,
    vpc_cidr: "",
    pinned_customer_gw_build_id: null,
    allow_beta_gateway_builds: false,
    auto_update_customer_gateways: false,
    enabled_options: ["GrandCentral"],
    allowed_gateway_types_models: null,
    allowed_customer_instances_models: null,
    diagnostics_ui_enabled: false,
    bug_tracker_enabled: false,
    companion_apps_enabled: false,
    advertising_enabled: false,
    mcp_enabled: false,
    audience_measurement_enabled: false,
    audience_measurement_ip_retention_days: null,
    measurement_presets: [],
    media_retention_days: null,
    audience_live_threshold_ms: null,
    auto_deploy: "none",
    enabled_ingest_regions: [],
    enabled_identity_providers: [],
  });
  const [primed, setPrimed] = useState(false);
  const [newRegionText, setNewRegionText] = useState("");
  // A form that creates an environment is open from the first render; one that edits it is filled in
  // when the tenant arrives (``primed``). Dirty = what it shows differs from what it was opened with,
  // and the way out of the page is held while it is.
  const { dirty, reset: markSaved } = useDirtyValues(comparableValues(state), isEdit ? primed : true);
  const guard = useUnsavedChanges(dirty);
  useEffect(() => {
    if (!initial || primed) return;
    setState({
      stage: initial.stage,
      aws_region: initial.awsRegion,
      is_production: initial.isProduction,
      // vpc_cidr is server-managed and no longer sent to the GUI — leave the
      // override field empty (empty = keep the stored value on save).
      vpc_cidr: "",
      pinned_customer_gw_build_id: initial.pinnedCustomerGwBuildId ?? null,
      allow_beta_gateway_builds: initial.allowBetaGatewayBuilds ?? false,
      auto_update_customer_gateways: initial.autoUpdateCustomerGateways ?? false,
      enabled_options: initial.enabledOptions,
      allowed_gateway_types_models: initial.allowedGatewayTypes
        ? Object.keys(initial.allowedGatewayTypes)
        : null,
      allowed_customer_instances_models: initial.allowedCustomerInstances
        ? Object.keys(initial.allowedCustomerInstances)
        : null,
      diagnostics_ui_enabled: initial.diagnosticsUiEnabled,
      bug_tracker_enabled: initial.bugTrackerEnabled,
      companion_apps_enabled: initial.companionAppsEnabled,
      advertising_enabled: initial.advertisingEnabled,
      mcp_enabled: initial.mcpEnabled,
      audience_measurement_enabled: initial.audienceMeasurementEnabled,
      audience_measurement_ip_retention_days: initial.audienceMeasurementIpRetentionDays ?? null,
      measurement_presets: initial.measurementPresets ?? [],
      media_retention_days: initial.mediaRetentionDays ?? null,
      audience_live_threshold_ms: initial.audienceLiveThresholdMs ?? null,
      auto_deploy: initial.autoDeploy ?? "none",
      enabled_ingest_regions: initial.enabledIngestRegions ?? [],
      // Only the directories the tenant still declares: an id it no longer
      // declares has no checkbox to clear, and the server refuses to save it.
      enabled_identity_providers: (initial.enabledIdentityProviders ?? []).filter((id) =>
        declaredDirectoryIds.includes(id),
      ),
    });
    setPrimed(true);
  }, [initial, primed, declaredDirectoryIds]);

  const mut = useMutation({
    mutationFn: async (productionKey?: string) => {
      const {
        stage: stageName,
        allowed_gateway_types_models,
        allowed_customer_instances_models,
        ...rest
      } = state;
      // Strip empty strings so the backend leaves the field unset. ``null`` is
      // preserved because it is the explicit "use latest" signal for
      // ``pinned_customer_gw_build_id``. ``enabled_ingest_regions`` rides along
      // in ``rest`` as a real string[] now — an empty array is never "" so it
      // survives this filter and sends ``[]`` (explicit clear), same as before.
      const cleaned = Object.fromEntries(
        Object.entries(rest).filter(([, v]) => v !== ""),
      ) as unknown as PutStageInput;
      cleaned.allowed_gateway_types = modelsToAllowMap(
        allowed_gateway_types_models ?? [],
      );
      cleaned.allowed_customer_instances = modelsToAllowMap(
        allowed_customer_instances_models ?? [],
      );
      // Option coupée : les presets partent vides. Omis, le PUT partiel les
      // conserverait, et l'API les refuse sans l'option. Le formulaire, lui,
      // les garde : recocher l'option rend la sélection d'avant.
      if (!state.audience_measurement_enabled) {
        cleaned.measurement_presets = [];
      }
      return await putStage(token, tenantId, stageName, cleaned, productionKey);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant", tenantId] });
      markSaved();
      guard.leave(`/tenants/${tenantId}`);
    },
  });

  // Retirer is_production sort le stage du champ de la clé : le backend
  // l'exige (cf. _guard_production_demotion). On la demande ici plutôt que
  // de laisser tomber un 403 sur un formulaire.
  const [showDemotion, setShowDemotion] = useState(false);
  const demoting = isEdit && initial?.isProduction === true && !state.is_production;

  // The title comes from the address, so loading and failing both keep it. A
  // failed load must not open the form: saved blank, it would overwrite the stage.
  // "Loading" includes PAUSED (the browser is offline): ``isLoading`` is false then,
  // the form would open blank with Save enabled, and a save queued offline is sent
  // when the network returns — a stage rewritten with the defaults.
  // The way back names the tenant (its name once it has loaded, its identifier until then).
  const header = (
    <PageHeader
      back={{ to: `/tenants/${tenantId}`, label: existing.data?.metadata.displayName ?? tenantId }}
      title={`${isEdit ? `Edit environment ${stage}` : "New environment"} · ${tenantId}`}
    />
  );
  if (isEdit && isLoadingOrPaused(existing)) return <FormPlaceholder header={header} />;
  // An environment of a tenant that does not exist (edit or new): the tenant is what is missing.
  if (existing.isLoadingError && isNotFound(existing.error)) return <TenantNotFound tenantId={tenantId} width="form" />;
  if (isEdit && existing.isLoadingError) {
    return <FormPlaceholder header={header} failure={loadFailure(existing)} />;
  }

  // URL preview reflects the backend derivation (compute_stage_domain): a
  // production stage lives at the tenant apex; a non-prod stage is a
  // hyphenated *sibling* `{stage}-{tenant}.pulsestation.io` (a single DNS
  // label, delegated flat from the root — 2026-07-06 convention), NOT a
  // nested `{stage}.{tenant}` subdomain.
  const urlHost = state.is_production
    ? `${tenantId}.${PULSESTATION_ROOT}`
    : `${state.stage || "<environment>"}-${tenantId}.${PULSESTATION_ROOT}`;

  // Auto-deploy options gate on is_production: a production stage may only
  // track production-channel builds (never "latest", which includes beta
  // feature-branch builds). Mirrors the backend ``_guard_auto_deploy``.
  const autoDeployOptions: { value: AutoDeployPolicy; label: string }[] =
    state.is_production
      ? [
          { value: "none", label: "Disabled (manual deployment)" },
          {
            value: "latest_prod_only",
            label: "Latest production build (master)",
          },
        ]
      : [
          { value: "none", label: "Disabled (manual deployment)" },
          {
            value: "latest",
            label: "Latest build (all branches, beta included)",
          },
          {
            value: "latest_prod_only",
            label: "Latest production build (master)",
          },
        ];
  // Defensive: keep the current value selectable even if it isn't normally
  // offered for this is_production flag (e.g. a legacy / out-of-band prod+latest
  // row). Otherwise the controlled dropdown renders blank and a save would
  // silently resubmit the stale value. The backend validator still gates
  // invalid combos, so the operator sees it and can pick a valid option.
  if (!autoDeployOptions.some((o) => o.value === state.auto_deploy)) {
    autoDeployOptions.unshift({
      value: state.auto_deploy,
      label: `${state.auto_deploy} (not recommended for this environment)`,
    });
  }

  // Customer-gateway update strategy is one of three mutually exclusive choices
  // mapped onto (auto_update_customer_gateways, pin): Manual (auto off, pin
  // null) / Auto-latest (auto on, pin null) / Auto-pinned (auto on, pin set).
  // The "pinned" strategy needs a valid build id — an empty pin is stripped
  // from the payload and would silently preserve the stored value, so block the
  // save until it is filled (belt-and-suspenders with the backend guard).
  const isPinnedStrategy =
    state.auto_update_customer_gateways &&
    state.pinned_customer_gw_build_id !== null;
  const pinValid =
    !isPinnedStrategy ||
    /^build-\d{8}-\d{1,8}$/.test(state.pinned_customer_gw_build_id ?? "");

  // The catalogue plus anything already saved on the stage that isn't in it
  // (a region added before this list existed, or out-of-band) — same "never
  // drop what's already there" rule as AllowlistField's orphan handling on
  // the tenant page. "eu-west-1" never shows here: it's the deployment
  // region, drawn separately below, disabled.
  const regionRows = Array.from(
    new Set([...INGEST_REGION_CATALOG, ...state.enabled_ingest_regions]),
  ).filter((r) => r !== "eu-west-1");
  const toggleRegion = (region: string) =>
    setState((s) => ({
      ...s,
      enabled_ingest_regions: s.enabled_ingest_regions.includes(region)
        ? s.enabled_ingest_regions.filter((r) => r !== region)
        : [...s.enabled_ingest_regions, region],
    }));
  const addRegion = () => {
    const region = newRegionText.trim().toLowerCase();
    if (!region) return;
    setState((s) => ({
      ...s,
      enabled_ingest_regions: s.enabled_ingest_regions.includes(region)
        ? s.enabled_ingest_regions
        : [...s.enabled_ingest_regions, region],
    }));
    setNewRegionText("");
  };

  // The tenant's declared directories (metadata.identityProviders), offered
  // here as sign-in checkboxes for this stage. Empty while the tenant query
  // is still loading — the section then shows the "none declared" message,
  // which is harmless: the checklist can't be checked before the tenant data
  // (and therefore ``primed``) arrives anyway.
  const tenantDirectories = existing.data?.metadata.identityProviders ?? [];

  // The presets this stage may activate, then — never dropped — any preset it
  // already activates that is no longer offered (removed from the tenant's
  // allowed list, or unknown): shown checked so that it can be removed; the
  // backend refuses to save it as it is. Unchecked, it stays in view (the
  // stage's saved list is read too) so that it can be checked back.
  // Only once the list has arrived: before, every activated preset would look
  // unknown. A failed refresh keeps the last list (``data`` stays).
  const presetItems = presets.data?.items ?? [];
  const offeredPresets = presetItems.filter((p) => presetAllowsTenant(p, tenantId));
  const orphanPresets = !presets.data ? [] : Array.from(new Set([...(initial?.measurementPresets ?? []), ...state.measurement_presets]))
    .filter((id) => !offeredPresets.some((p) => p.presetId === id))
    .map((id) => ({ id, known: presetItems.find((p) => p.presetId === id) }));
  const ipRetention = state.audience_measurement_ip_retention_days ?? 90;
  const togglePreset = (id: string, on: boolean) =>
    setState((s) => ({
      ...s,
      measurement_presets: on
        ? [...s.measurement_presets.filter((p) => p !== id), id]
        : s.measurement_presets.filter((p) => p !== id),
    }));

  return (
    <Page width="form">
      {header}

      <form
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          // Guard here too, not just on the disabled button: an Enter keypress
          // in a text field triggers implicit form submission, which can bypass
          // a disabled submit button. Without this, an Auto-pinned strategy with
          // an empty build id would submit pin="" — stripped from the payload —
          // and silently preserve the stored pin.
          if (!pinValid) return;
          if (demoting) {
            // A failure left by an earlier plain Save must not resurface in the
            // modal, which renders ``mut.error`` itself.
            mut.reset();
            setShowDemotion(true);
            return;
          }
          mut.mutate(undefined);
        }}
      >
        <div className="card space-y-5">
          <Field label="Environment name">
            <Input
              mono
              value={state.stage}
              required
              disabled={isEdit}
              // Mirrors ``_STAGE_NAME_RE`` server-side: strict slug because the
              // name is baked into DNS labels and other infra identifiers.
              pattern="[a-z][a-z0-9]{0,31}"
              title="Lowercase letters and digits only, starting with a letter (≤32 characters). E.g.: dev, staging, prod."
              onChange={(e) => setState((s) => ({ ...s, stage: e.target.value }))}
            />
          </Field>

          <div>
            <Checkbox
              label="Production environment"
              checked={state.is_production ?? false}
              disabled={prodLocked}
              onChange={(e) =>
                setState((s) => ({
                  ...s,
                  is_production: e.target.checked,
                  // A production stage can't auto-deploy "latest" (any-channel,
                  // beta included) — the backend validator rejects it. Coerce
                  // to manual so the dropdown + payload stay valid.
                  auto_deploy:
                    e.target.checked && s.auto_deploy === "latest"
                      ? "none"
                      : s.auto_deploy,
                }))
              }
            />
            {prodLocked && (
              <p className="mt-1 text-xs text-warning-fg">
                This tenant already has a production environment:{" "}
                <code className="font-mono">{otherProdStage!.stage}</code>. Only one
                production environment is allowed per tenant.
              </p>
            )}
            <p className="mt-1 text-xs text-ink-muted">
              URL:{" "}
              <code className="font-mono">https://{urlHost}</code>
            </p>
          </div>

          <Fieldset legend="Enabled options" className="border-t border-line pt-4">
            <div className="mt-2 flex flex-wrap gap-2">
              {OPTIONS.map((opt) => {
                const checked = state.enabled_options.includes(opt);
                return (
                  <ChoiceChip
                    key={opt}
                    label={opt}
                    checked={checked}
                    onChange={() =>
                      setState((s) => ({
                        ...s,
                        enabled_options: checked
                          ? s.enabled_options.filter((o) => o !== opt)
                          : [...s.enabled_options, opt],
                      }))
                    }
                  />
                );
              })}
            </div>
          </Fieldset>

          <div className="border-t border-line pt-4">
            <Checkbox
              label="Diagnostics section (GUI)"
              description="Shows the Diagnostics section (SCTE timing tool…) in this environment's GUI. Takes effect on the next deployment. Reserve for test environments."
              checked={state.diagnostics_ui_enabled}
              onChange={(e) =>
                setState((s) => ({
                  ...s,
                  diagnostics_ui_enabled: e.target.checked,
                }))
              }
            />
          </div>

          <div className="border-t border-line pt-4">
            <Checkbox
              label="Bug tracker (GUI)"
              description="Shows the bug report button in this environment's GUI; reports create GitHub issues, triaged automatically. Takes effect on the next deployment."
              checked={state.bug_tracker_enabled}
              onChange={(e) =>
                setState((s) => ({
                  ...s,
                  bug_tracker_enabled: e.target.checked,
                }))
              }
            />
          </div>

          <div className="border-t border-line pt-4">
            <Checkbox
              label="Companion apps (GUI)"
              description="Shows the companion apps screens in this environment's GUI: paired TVs, pairing approval and the phone app QR code. Pairing keeps working server-side either way. Takes effect on the next deployment."
              checked={state.companion_apps_enabled}
              onChange={(e) =>
                setState((s) => ({
                  ...s,
                  companion_apps_enabled: e.target.checked,
                }))
              }
            />
          </div>

          <div className="border-t border-line pt-4">
            <Checkbox
              label="Advertising"
              description="Lets this environment serve ads. Its CDN logs then record each listener's IP address and User-Agent, for all of its traffic, because the ad beacons are sent from those logs and the ad server needs both. Leave it off where no stream serves ads. Takes effect on the next deployment."
              checked={state.advertising_enabled}
              onChange={(e) =>
                setState((s) => ({
                  ...s,
                  advertising_enabled: e.target.checked,
                }))
              }
            />
          </div>

          <div className="border-t border-line pt-4">
            <Checkbox
              label="MCP server (GrandCentral)"
              description="Deploys the MCP server (Model Context Protocol) that exposes the GrandCentral API as MCP tools for compatible clients (Claude, IDE agents…). Unlike the options above, this deploys real infrastructure (Lambda + dedicated API). Takes effect on the next deployment."
              checked={state.mcp_enabled}
              onChange={(e) =>
                setState((s) => ({
                  ...s,
                  mcp_enabled: e.target.checked,
                }))
              }
            />
          </div>

          <div className="border-t border-line pt-4">
            <Checkbox
              label="Audience Measurement"
              description="Collects what audience measurement needs (listener IP address, user agent) on this environment's streams, and pushes the audience classification and the presets activated below. Deploys real infrastructure. Takes effect on the next deployment."
              checked={state.audience_measurement_enabled}
              onChange={(e) =>
                setState((s) => ({ ...s, audience_measurement_enabled: e.target.checked }))
              }
            />
            {state.audience_measurement_enabled && (
              <div className="mt-3 space-y-4 pl-6">
                <Field
                  label="IP retention (days)"
                  help="How long the audience facts keep full IP addresses, 15 to 90 days. A preset may require more. Leave empty for the default (90 days)."
                >
                  <Input
                    type="number"
                    min={15}
                    max={90}
                    step={1}
                    placeholder="Default (90)"
                    value={state.audience_measurement_ip_retention_days ?? ""}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const n = Number(raw);
                      setState((s) => ({
                        ...s,
                        audience_measurement_ip_retention_days:
                          raw === "" || Number.isNaN(n) ? null : n,
                      }));
                    }}
                  />
                </Field>

                <Fieldset
                  legend="Measurement presets"
                  help={
                    <>
                      Rule sets applied to this environment's audience, managed under{" "}
                      <Link to="/measurement-presets" className="underline">Measurement presets</Link>.
                    </>
                  }
                >
                  {presets.isLoadingError ? (
                    <div className="mt-2">
                      <ErrorBox
                        error={presets.error}
                        title="Could not load the measurement presets"
                        onRetry={() => void presets.refetch()}
                        retrying={presets.isFetching}
                      />
                    </div>
                  ) : !presets.data ? (
                    <LoadingRegion label="Loading the measurement presets…" className="mt-2 space-y-2">
                      <Skeleton className="h-4 w-1/3" />
                      <Skeleton className="h-4 w-1/2" />
                    </LoadingRegion>
                  ) : (
                    <div className="mt-2 space-y-2">
                      <RefreshFailed query={presets} />
                      {offeredPresets.length === 0 && orphanPresets.length === 0 && (
                        <p className="text-sm text-ink-muted">No measurement preset is offered to this tenant.</p>
                      )}
                      {offeredPresets.map((p) => {
                        const checked = state.measurement_presets.includes(p.presetId);
                        return (
                          <div key={p.presetId}>
                            <Checkbox
                              label={p.name}
                              description={
                                <>
                                  <span className="font-mono">{p.presetId}</span> · version {p.version} ·{" "}
                                  {p.exportFormat} · keeps IP addresses at least {p.minIpRetentionDays} days
                                </>
                              }
                              checked={checked}
                              onChange={(e) => togglePreset(p.presetId, e.target.checked)}
                            />
                            {checked && ipRetention < p.minIpRetentionDays && (
                              <p className="mt-1 pl-6 text-xs text-warning-fg">
                                {p.name} requires an IP retention of at least {p.minIpRetentionDays} days; this
                                environment keeps IP addresses {ipRetention} days.
                              </p>
                            )}
                          </div>
                        );
                      })}
                      {orphanPresets.map(({ id, known }) => (
                        <Checkbox
                          key={id}
                          label={known?.name ?? id}
                          description={
                            <>
                              <span className="font-mono">{id}</span> ·{" "}
                              {known ? "Not offered to this tenant any more" : "Unknown preset"}: the environment cannot be saved while it is checked
                            </>
                          }
                          checked={state.measurement_presets.includes(id)}
                          onChange={(e) => togglePreset(id, e.target.checked)}
                        />
                      ))}
                    </div>
                  )}
                </Fieldset>
              </div>
            )}
          </div>

          <Fieldset
            legend="Sign-in with company directories"
            help="Directories offered on this environment's sign-in page. The sign-in pool changes on save; the environment's GUI shows them after its next deployment."
            className="border-t border-line pt-4"
          >
            {tenantDirectories.length === 0 ? (
              <p className="mt-2 text-sm text-ink-muted">
                No company directory is declared for this tenant.{" "}
                <Link to={`/tenants/${tenantId}/identity-providers`} className="underline">Declare one</Link>
              </p>
            ) : (
              <div className="mt-2 space-y-2">
                {tenantDirectories.map((p) => (
                  <Checkbox
                    key={p.id}
                    label={p.displayName}
                    description={p.roleSource === "directory" ? "Roles come from the directory." : "Roles are granted by invitation."}
                    checked={state.enabled_identity_providers.includes(p.id)}
                    onChange={(e) =>
                      setState((s) => ({
                        ...s,
                        enabled_identity_providers: e.target.checked
                          ? [...s.enabled_identity_providers, p.id]
                          : s.enabled_identity_providers.filter((id) => id !== p.id),
                      }))
                    }
                  />
                ))}
              </div>
            )}
          </Fieldset>

          <Field
            className="border-t border-line pt-4"
            label="Media retention (days)"
            help="Retention period for raw PCM segments in S3 (= catch-up / DVR window), and therefore the main lever on S3 cost. 1 to 30 days: the input history a replay relies on is kept 30 days. Leave empty to inherit the tenant's default. Takes effect on the next deployment."
          >
            <Input
              type="number"
              min={1}
              max={30}
              placeholder="Tenant default (7)"
              value={state.media_retention_days ?? ""}
              onChange={(e) => {
                const raw = e.target.value;
                const n = Number(raw);
                // Empty (or any unparseable value) means "inherit" → null. Guard
                // NaN explicitly so a stray non-numeric value can't be sent as a
                // silent null that clears an existing override.
                setState((s) => ({
                  ...s,
                  media_retention_days:
                    raw === "" || Number.isNaN(n) ? null : n,
                }));
              }}
            />
          </Field>

          <Field
            label="Live audience threshold (seconds)"
            help="How far behind the live edge of their stream a listener can be and still count as live in audience figures. A stream's own configured offset is not counted as delay. Leave empty to inherit the tenant's default (30 s). Takes effect on the next deployment."
          >
            <Input
              type="number"
              min={5}
              max={600}
              step={1}
              placeholder="Tenant default (30)"
              value={
                state.audience_live_threshold_ms === null
                  ? ""
                  : state.audience_live_threshold_ms / 1000
              }
              onChange={(e) => {
                const raw = e.target.value;
                const n = Number(raw);
                // Same guard as the retention field: empty or unparseable means
                // "inherit" (null), never a silent value. Seconds typed, ms stored.
                setState((s) => ({
                  ...s,
                  audience_live_threshold_ms:
                    raw === "" || Number.isNaN(n) ? null : Math.round(n * 1000),
                }));
              }}
            />
          </Field>

          <Fieldset
            className="border-t border-line pt-4"
            legend="Enabled ingest regions"
            help={
              <>
                Additional AWS regions where a customer gateway can ingest.
                The dataplane deploys a regional encoder + PCM bucket there
                and validates a gateway's region at creation. The deployment
                region is always available — only the <em>additional</em>{" "}
                regions need a toggle. Takes effect on the next deployment.
              </>
            }
          >
            <div className="mt-2 flex flex-col gap-2">
              <div className="rounded border border-line bg-sunken p-3">
                <Checkbox
                  label={<span className="font-mono">eu-west-1</span>}
                  description="Deployment region — always available, no toggle needed."
                  checked
                  disabled
                  onChange={() => {}}
                />
              </div>
              {regionRows.map((region) => (
                <div key={region} className="rounded border border-line p-3">
                  <Checkbox
                    label={<span className="font-mono">{region}</span>}
                    checked={state.enabled_ingest_regions.includes(region)}
                    onChange={() => toggleRegion(region)}
                  />
                </div>
              ))}
              <div className="mt-1 flex items-center gap-2">
                <Input
                  type="text"
                  mono
                  aria-label="Add another ingest region"
                  placeholder="Add another region id, e.g. ap-southeast-1"
                  pattern="[a-z]{2}-[a-z]+-\d+"
                  title="An AWS region id, e.g. eu-west-3"
                  value={newRegionText}
                  onChange={(e) => setNewRegionText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addRegion();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={!newRegionText.trim()}
                  onClick={addRegion}
                >
                  Add
                </Button>
              </div>
            </div>
          </Fieldset>

          <Field
            className="border-t border-line pt-4"
            label="Automatic deployment"
            help={`Automatically redeploys this environment as soon as a matching build is registered (and immediately upon activation). "All branches" also follows beta builds from feature branches; "production" only follows commits on master. A production environment can only follow production builds.`}
          >
            <Select
              value={state.auto_deploy}
              onChange={(e) =>
                setState((s) => ({
                  ...s,
                  auto_deploy: e.target.value as AutoDeployPolicy,
                }))
              }
              options={autoDeployOptions}
            />
          </Field>

          <GatewaySelector
            legend="Allowed cloud instances"
            help="Empty = no cloud instance allowed for this environment."
            options={cloudInstances}
            loading={catalog.isLoading}
            selected={state.allowed_gateway_types_models}
            onChange={(next) =>
              setState((s) => ({
                ...s,
                allowed_gateway_types_models: next.length === 0 ? null : next,
              }))
            }
          />
          <GatewaySelector
            legend="Allowed customer instances"
            help="Empty = no customer instance allowed for this environment."
            options={customerInstances}
            loading={catalog.isLoading}
            selected={state.allowed_customer_instances_models}
            onChange={(next) =>
              setState((s) => ({
                ...s,
                allowed_customer_instances_models: next.length === 0 ? null : next,
              }))
            }
          />

          <div className="border-t border-line pt-4">
            {/* Customer-gateway update strategy — one mutually exclusive choice.
                The three legacy checkboxes (pin / follow-latest / auto on-off)
                collapsed into three strategies over (auto_update, pin), so the
                ambiguous "pin without auto-update" state is unrepresentable. */}
            <Fieldset legend="Customer gateway updates">
              <div className="mt-2 space-y-2">
                <Radio
                  name="gw-update-strategy"
                  label={<span className="font-medium">Manual</span>}
                  description="The environment doesn't manage versions. Each gateway is updated by hand from the dataplane (build selection or per-gateway auto-update)."
                  checked={!state.auto_update_customer_gateways}
                  onChange={() =>
                    setState((s) => ({
                      ...s,
                      auto_update_customer_gateways: false,
                      pinned_customer_gw_build_id: null,
                    }))
                  }
                />
                <Radio
                  name="gw-update-strategy"
                  label={
                    <span className="font-medium">
                      Auto — latest production version
                    </span>
                  }
                  description="Every 15 minutes, the dataplane converges each gateway to the latest production build. A gateway whose last update failed is not automatically retried."
                  checked={
                    state.auto_update_customer_gateways &&
                    state.pinned_customer_gw_build_id === null
                  }
                  onChange={() =>
                    setState((s) => ({
                      ...s,
                      auto_update_customer_gateways: true,
                      pinned_customer_gw_build_id: null,
                    }))
                  }
                />
                <Radio
                  name="gw-update-strategy"
                  label={<span className="font-medium">Auto — pinned version</span>}
                  description="All gateways stay on a specific build (below) until explicitly changed."
                  checked={
                    state.auto_update_customer_gateways &&
                    state.pinned_customer_gw_build_id !== null
                  }
                  onChange={() =>
                    setState((s) => ({
                      ...s,
                      auto_update_customer_gateways: true,
                      pinned_customer_gw_build_id:
                        s.pinned_customer_gw_build_id ?? "",
                    }))
                  }
                />
              </div>
            </Fieldset>
            {isPinnedStrategy && (
              <Field
                className="mt-2"
                label="Pinned version"
                error={
                  !pinValid
                    ? "Enter a build in the format build-YYYYMMDD-N."
                    : undefined
                }
              >
                <Input
                  mono
                  value={state.pinned_customer_gw_build_id ?? ""}
                  pattern="^build-[0-9]{8}-[0-9]{1,8}$"
                  title="Format build-YYYYMMDD-N."
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      pinned_customer_gw_build_id: e.target.value,
                    }))
                  }
                />
              </Field>
            )}

            <Checkbox
              className="mt-4"
              label="Allow beta builds for customer gateways"
              description="Beta builds come from gw-docker branches other than main. Unchecked: a beta version can't be pinned here, and the environment hides and rejects beta builds (gateway listing and updates)."
              checked={state.allow_beta_gateway_builds}
              onChange={(e) =>
                setState((s) => ({
                  ...s,
                  allow_beta_gateway_builds: e.target.checked,
                }))
              }
            />
          </div>

          {/* Infra-level knobs the operator rarely needs — sensible defaults
              apply when left untouched. Folded away from the nominal flow. */}
          <details className="border-t border-line pt-4">
            <summary className="cursor-pointer text-sm font-medium text-ink">
              Advanced options
            </summary>
            <div className="mt-3 space-y-4">
              <Field label="Region">
                <Input
                  value={state.aws_region ?? ""}
                  onChange={(e) =>
                    setState((s) => ({ ...s, aws_region: e.target.value }))
                  }
                />
              </Field>
              <Field label="Network range (VPC CIDR)">
                <Input
                  mono
                  value={state.vpc_cidr ?? ""}
                  onChange={(e) =>
                    setState((s) => ({ ...s, vpc_cidr: e.target.value }))
                  }
                />
              </Field>
              <p className="text-xs text-ink-muted">
                {isEdit
                  ? "Leave empty to keep the current value."
                  : "Leave empty to use the default value."}
              </p>
            </div>
          </details>

          <p className="border-t border-line pt-3 text-xs text-ink-muted">
            Access to the tenant's infrastructure is configured automatically
            once the installation link is used, after saving.
          </p>
        </div>

        {/* While the demotion modal is up it shows this error itself. */}
        <FormActionBar
          dirty={dirty}
          submitLabel="Save"
          pending={mut.isPending}
          disabled={!pinValid}
          disabledReason="Enter a build in the format build-YYYYMMDD-N."
          onCancel={() => navigate(-1)}
          error={!showDemotion && <MutationError mutation={mut} title="Could not save the environment" />}
        />
      </form>

      {showDemotion && (
        <ConfirmModal
          title={`Remove production status from ${state.stage}`}
          confirmLabel="Remove production status"
          pendingLabel="Saving…"
          danger
          productionKey
          pending={mut.isPending}
          error={mut.error}
          onCancel={() => {
            mut.reset();
            setShowDemotion(false);
          }}
          // The modal stays up while the save runs (its ``pending`` label says
          // so) and until it succeeds — the page then navigates away. A wrong
          // production key is the expected failure: it must show here, not in
          // a banner at the top of a long form the operator has scrolled past.
          onConfirm={(key) => mut.mutate(key)}
        >
          <p>
            This environment will no longer be treated as production. Teardown
            actions on it will stop asking for the production key.
          </p>
        </ConfirmModal>
      )}

      {/* Hors du <form> ci-dessus : IntegrationForm porte son propre
          formulaire (envoi séparé, vers une autre route), et un formulaire
          imbriqué est invalide en HTML — le submit du stage s'en trouverait
          cassé. Réservé à l'édition : la surcharge s'attache à un
          environnement qui existe déjà. */}
      {/* Un fournisseur redéfinissable = une carte, prise du catalogue backend.
          Les deux cartes qui vivaient ici en dur nommaient « Identification
          musicale » et « Catalogue musical » sans jamais dire chez QUI, et
          leurs titres avaient déjà divergé de ceux de Services tiers. */}
      {isEdit &&
        stage &&
        overridable.map((integration) => (
          <div key={integration.provider} className="card mt-6">
            <div className="flex flex-wrap items-baseline gap-x-3">
              <h2 className="text-lg font-semibold text-ink">
                {integration.label}
              </h2>
              <span className="text-sm font-medium text-ink-muted">
                {integration.vendor}
              </span>
            </div>
            <p className="mb-4 mt-1 text-sm text-ink-muted">
              By default, this environment uses the shared account defined in
              Integrations. Fill in the fields to assign it a dedicated one.
            </p>
            <IntegrationForm
              provider={integration.provider}
              scope={{ tenantId, stage }}
            />
          </div>
        ))}
    </Page>
  );
}

function GatewaySelector({
  legend,
  help,
  options,
  loading,
  selected,
  onChange,
}: {
  legend: string;
  help: string;
  options: string[];
  loading: boolean;
  selected: string[] | null;
  onChange: (next: string[]) => void;
}) {
  const list = selected ?? [];
  return (
    <Fieldset
      legend={list.length > 0 ? `${legend} (${list.length} selected)` : legend}
      help={help}
    >
      {loading && (
        <p className="mt-2 text-xs text-ink-muted">Loading catalog…</p>
      )}
      {!loading && options.length === 0 && (
        <p className="mt-2 text-xs text-ink-muted">
          No matching entry — add one to the catalog.
        </p>
      )}
      <div className="mt-2 grid grid-cols-2 gap-2">
        {options.map((name) => {
          const checked = list.includes(name);
          return (
            <div key={name} className="rounded border border-line p-3">
              <Checkbox
                label={<span className="font-mono">{name}</span>}
                checked={checked}
                onChange={() => {
                  const next = checked
                    ? list.filter((o) => o !== name)
                    : [...list, name];
                  onChange(next);
                }}
              />
            </div>
          );
        })}
      </div>
    </Fieldset>
  );
}
